/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { PropertyListener } from '@vsolv/dev-kit/rx';
import { Property, PropertySet } from '@vsolv/packages/properties/domain';
import { BehaviorSubject, map } from 'rxjs';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'vs-property-value-item',
  templateUrl: './property-value-item.component.html',
  standalone: false,
})
export class PropertyValueItemComponent {
  @PropertyListener('assignment') assignment$ = new BehaviorSubject<
    Property.ObjectPropertyAssignment | PropertySet.PropertyAssignment | null
  >(null);
  @Input() assignment!: Property.ObjectPropertyAssignment | PropertySet.PropertyAssignment;

  @PropertyListener('propertyValue') propertyValue$ = new BehaviorSubject<any>(null);
  @Input() propertyValue: any;
  @Input() showHidden = false;
  @Input() showBadge = true;
  @Input() showLiteralBoolean = false;
  /** When true, render a Required placeholder if this assignment has no value. */
  @Input() showMissing = false;
  /** When true, list missing required children of object properties by name. */
  @Input() showMissingRequired = false;
  @Input() missingIds: ReadonlySet<string> = new Set();
  expanded = false;

  isMissingValue(value: unknown): boolean {
    return value === undefined || value === null || value === '';
  }

  childObjects$ = this.assignment$.pipe(
    map(assignment => {
      if (!assignment) return [];
      const property = assignment.property!;
      if (property.type !== Property.PropertyType.OBJECT) return [];

      return (
        (property as Property.ObjectModel).properties?.filter(
          assignment => assignment.property?.type === Property.PropertyType.OBJECT
        ) ?? []
      );
    })
  );

  toggleExpanded() {
    this.expanded = !this.expanded;
  }

  /** Required non-object children of this object that have no value. */
  getMissingChildren(
    property: Property.ObjectModel,
    objectValue: Record<string, unknown> | null | undefined
  ): Property.ObjectPropertyAssignment[] {
    if (!this.showMissingRequired) return [];

    return (property.properties ?? []).filter(child => {
      if (!child.property || child.hidden) return false;
      if (child.property.type === Property.PropertyType.OBJECT) return false;

      const value = objectValue?.[child.property.valueKey];
      if (!this.isMissingValue(value)) return false;

      return child.required || this.missingIds.has(child.property.id);
    });
  }

  childLabel(child: Property.ObjectPropertyAssignment): string {
    return child.displayLabel || child.property?.name || child.propertyId;
  }

  hasMissingChildren(property: Property.Model<Property.PropertyType>, value: unknown): boolean {
    if (property.type !== Property.PropertyType.OBJECT) return false;
    return this.getMissingChildren(property as Property.ObjectModel, value as Record<string, unknown>).length > 0;
  }
}
